// PDF export for the IVR Weekly Synopsis -- same "open a print window, let
// the browser's Save as PDF handle it" approach as printPayslip in
// payslip.ts, so there's no new PDF-library dependency. Meant to be handed
// to the IVR developer, so it includes the admin's tracking notes/status on
// each suggestion (useful context: "already fixed" tells them not to
// bother) alongside the findings themselves.
import type { IvrWeeklySynopsis, IvrSynopsisSuggestion } from '@/lib/ivrWeeklySynopsis';
import { formatDateTime } from '@/lib/dates';

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  done: 'Done',
  dismissed: 'Dismissed',
};

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function buildSynopsisPdfHtml(synopsis: IvrWeeklySynopsis, suggestions: IvrSynopsisSuggestion[]): string {
  const period = `${synopsis.week_start} to ${synopsis.week_end}`;
  const addressTrouble = (synopsis.address_trouble_pickup_count ?? 0) + (synopsis.address_trouble_destination_count ?? 0);

  const statHtml = (label: string, value: number, sub?: string) => `
    <div class="stat">
      <div class="stat-value">${value}</div>
      <div class="stat-label">${esc(label)}</div>
      ${sub ? `<div class="stat-sub">${esc(sub)}</div>` : ''}
    </div>`;

  const sectionsHtml = synopsis.sections
    .map((s) => `<h3 class="section-title">${esc(s.heading)}</h3><div class="prose">${s.body_html}</div>`)
    .join('');

  const transferReasonsHtml = synopsis.transfer_reasons.length
    ? `<h3 class="section-title">Transfer Reasons</h3>
       <table class="grid">
         <thead><tr><th>Reason</th><th class="num">Count</th></tr></thead>
         <tbody>
           ${synopsis.transfer_reasons.map((r) => `<tr><td>${esc(r.label)}</td><td class="num">${r.count}</td></tr>`).join('')}
         </tbody>
       </table>`
    : '';

  const suggestionsFor = (category: 'train' | 'engineer') =>
    suggestions
      .filter((s) => s.category === category)
      .map(
        (s) => `
        <li class="suggestion">
          <div class="suggestion-text">${esc(s.suggestion)}</div>
          <div class="suggestion-meta">
            <span class="badge">${STATUS_LABEL[s.status] || s.status}</span>
            ${s.admin_note ? `<span class="note">${esc(s.admin_note)}</span>` : ''}
          </div>
        </li>`,
      )
      .join('');

  const trainHtml = suggestionsFor('train');
  const engineerHtml = suggestionsFor('engineer');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>IVR Weekly Synopsis — ${period}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 28px; }
  .header { text-align: center; margin-bottom: 18px; }
  .header h1 { margin: 0; font-size: 22px; }
  .header .sub { color: #555; font-size: 13px; margin-top: 4px; }

  .stats { display: flex; gap: 10px; margin: 20px 0; }
  .stat { flex: 1; border: 1px solid #ddd; border-radius: 8px; padding: 10px 12px; }
  .stat-value { font-size: 22px; font-weight: 700; }
  .stat-label { font-size: 11px; color: #666; margin-top: 2px; }
  .stat-sub { font-size: 10px; color: #888; margin-top: 2px; }

  .section-title { font-size: 15px; margin: 22px 0 8px; border-bottom: 2px solid #16a34a; padding-bottom: 4px; }
  .prose p { margin: 0 0 8px; font-size: 13px; line-height: 1.5; }
  .prose ul { margin: 0 0 8px; padding-left: 20px; font-size: 13px; line-height: 1.5; }

  table.grid { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px; }
  table.grid th, table.grid td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
  table.grid th { background: #f3f3f3; }
  table.grid td.num, table.grid th.num { text-align: right; }

  .suggestion-cols { display: flex; gap: 20px; margin-top: 8px; }
  .suggestion-col { flex: 1; }
  .suggestion-col h4 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 8px; }
  .suggestion-col.train h4 { color: #16a34a; }
  .suggestion-col.engineer h4 { color: #d97706; }
  ul.suggestion-list { list-style: none; margin: 0; padding: 0; }
  li.suggestion { border: 1px solid #e2e2e2; border-radius: 6px; padding: 8px 10px; margin-bottom: 8px; }
  .suggestion-text { font-size: 12.5px; line-height: 1.4; }
  .suggestion-meta { margin-top: 6px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .badge { display: inline-block; font-size: 10px; padding: 2px 7px; border-radius: 999px; background: #eee; color: #444; }
  .note { font-size: 11px; color: #666; font-style: italic; }

  .foot { margin-top: 32px; text-align: center; color: #888; font-size: 11px; }
  @media print { body { padding: 12px; } }
</style>
</head>
<body>
  <div class="header">
    <h1>IVR Weekly Synopsis</h1>
    <div class="sub">${period} &middot; ${synopsis.calls_read} of ${synopsis.calls_total} calls read</div>
  </div>

  <div class="stats">
    ${statHtml('Transferred', synopsis.transferred_count)}
    ${statHtml('No Booking Attempt', synopsis.zero_attempt_count)}
    ${statHtml('Completed', synopsis.completed_count)}
    ${statHtml(
      'Address Trouble',
      addressTrouble,
      `${synopsis.address_trouble_pickup_count ?? 0} pickup, ${synopsis.address_trouble_destination_count ?? 0} destination`,
    )}
  </div>

  ${sectionsHtml}
  ${transferReasonsHtml}

  ${
    trainHtml || engineerHtml
      ? `<h3 class="section-title">Suggestions</h3>
         <div class="suggestion-cols">
           <div class="suggestion-col train">
             <h4>Good fit for bot training</h4>
             <ul class="suggestion-list">${trainHtml || '<li class="suggestion"><div class="suggestion-text">None.</div></li>'}</ul>
           </div>
           <div class="suggestion-col engineer">
             <h4>Needs engineering</h4>
             <ul class="suggestion-list">${engineerHtml || '<li class="suggestion"><div class="suggestion-text">None.</div></li>'}</ul>
           </div>
         </div>`
      : ''
  }

  <div class="foot">Generated ${formatDateTime(synopsis.generated_at)}</div>
</body>
</html>`;
}

/** Opens the synopsis in a print window so the user can "Save as PDF" --
 *  same approach as printPayslip in payslip.ts. */
export function printSynopsis(html: string): void {
  const w = window.open('', '_blank', 'width=820,height=1000');
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
  setTimeout(() => {
    w.focus();
    w.print();
  }, 350);
}
